interface SendCodeResponse {
  success: boolean;
  error?: string;
  details?: string;
}
import { sendErrorToSlack } from '@/utils/slack-error-reporter';

interface VerifyCodeResponse {
  success: boolean;
  error?: string;
  details?: string;
}

export async function sendVerificationCode(phoneNumber: string, locale?: string): Promise<SendCodeResponse> {
  try {
    const provider = (process.env.NEXT_PUBLIC_MFA_PROVIDER || 'twilio').toLowerCase();
    const isTwilio = provider === 'twilio';
    const endpoint = isTwilio ? '/api/auth/mfa/send-code-twilio' : '/api/auth/mfa/send-code';

    const body: any = { phoneNumber };
    if (isTwilio && locale) body.locale = locale; // locale only used by Twilio Verify

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send verification code');
    }

    return { success: true };
  } catch (error: any) {
    try {
      const provider = (process.env.NEXT_PUBLIC_MFA_PROVIDER || 'twilio').toLowerCase();
      const endpoint = provider === 'twilio' ? '/api/auth/mfa/send-code-twilio' : '/api/auth/mfa/send-code';
      // Fire-and-forget
      sendErrorToSlack({
        error: { message: error?.message || 'Failed to send verification code', stack: error?.stack, type: 'SendCodeError' },
        context: { endpoint, timestamp: new Date().toISOString(), environment: process.env.NODE_ENV || 'development' },
        requestPayload: { phoneNumberMasked: phoneNumber?.replace(/.(?=.{4}$)/g, '*'), provider, locale }
      } as any);
    } catch (_) {}
    return {
      success: false,
      error: error.message,
      details: error.toString()
    };
  }
}

export async function verifyCode(phoneNumber: string, code: string, locale: string = 'en'): Promise<VerifyCodeResponse> {
  try {
    const provider = (process.env.NEXT_PUBLIC_MFA_PROVIDER || 'twilio').toLowerCase();
    const isTwilio = provider === 'twilio';
    const endpoint = isTwilio ? '/api/auth/mfa/verify-code-twilio' : '/api/auth/mfa/verify-code';

    const body: any = { phoneNumber, code };
    if (isTwilio) body.locale = locale; // pass locale only for Twilio (for localized errors/templates)

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Invalid verification code');
    }

    return { success: true };
  } catch (error: any) {
    try {
      const provider = (process.env.NEXT_PUBLIC_MFA_PROVIDER || 'twilio').toLowerCase();
      const endpoint = provider === 'twilio' ? '/api/auth/mfa/verify-code-twilio' : '/api/auth/mfa/verify-code';
      // Fire-and-forget
      sendErrorToSlack({
        error: { message: error?.message || 'Failed to verify code', stack: error?.stack, type: 'VerifyCodeError' },
        context: { endpoint, timestamp: new Date().toISOString(), environment: process.env.NODE_ENV || 'development' },
        requestPayload: { phoneNumberMasked: phoneNumber?.replace(/.(?=.{4}$)/g, '*'), provider }
      } as any);
    } catch (_) {}
    return {
      success: false,
      error: error.message,
      details: error.toString()
    };
  }
}
